import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Pin, MessageSquare, ThumbsUp, Users, Eye, CheckCircle, AlertCircle, Search, Trash2 }from 'lucide-react';

const MOCK_POSTS = [
  {
    id: 1, author: 'Sophie M.', avatar: 'SM', category: 'Témoignages',
    title: 'Retour de séjour Marrakech — une expérience qui change tout',
    excerpt: 'Je voulais partager mon expérience après le séjour de ma mère à Marrakech. L\'équipe M-CareLink a été d\'un soutien exceptionnel...',
    likes: 12, comments: 5, status: 'published', pinned: true, date: '2026-01-18'
  },
  {
    id: 2, author: 'Jean-Pierre L.', avatar: 'JL', category: 'Conseils',
    title: 'Comment préparer son aidé au premier séjour de répit ?',
    excerpt: 'Après deux séjours avec M-CareLink, voici mes conseils pour préparer psychologiquement la personne aidée au départ...',
    likes: 8, comments: 3, status: 'published', pinned: false, date: '2026-01-20'
  },
  {
    id: 3, author: 'Marie B.', avatar: 'MB', category: 'Questions',
    title: 'Remboursement APA — quelqu\'un a des infos ?',
    excerpt: 'Bonjour à tous, je cherche des informations sur la prise en charge APA pour les séjours au Maroc...',
    likes: 4, comments: 7, status: 'pending', pinned: false, date: '2026-01-22'
  },
  {
    id: 4, author: 'Ahmed B.', avatar: 'AB', category: 'Témoignages',
    title: 'Séjour Agadir — photos et impressions',
    excerpt: 'De retour de notre séjour de répit à Agadir avec ma mère. Je partage quelques impressions et photos du quotidien...',
    likes: 15, comments: 9, status: 'published', pinned: false, date: '2026-01-25'
  },
  {
    id: 5, author: 'Lucie C.', avatar: 'LC', category: 'Annonces',
    title: '[Groupe de parole] Rencontre virtuelle — 15 février',
    excerpt: 'L\'équipe M-CareLink organise un groupe de parole en ligne pour les aidants. Inscriptions ouvertes...',
    likes: 20, comments: 11, status: 'published', pinned: true, date: '2026-01-28'
  },
];

const CATEGORIES = ['Tous', 'Témoignages', 'Conseils', 'Questions', 'Annonces'];
const CAT_COLORS = { Témoignages: 'bg-[#FFF3E8] text-[#F47B20]', Conseils: 'bg-green-50 text-green-700', Questions: 'bg-blue-50 text-blue-700', Annonces: 'bg-purple-50 text-purple-700' };

export default function CommunityModule() {
  const [posts, setPosts] = useState(MOCK_POSTS);
  const [filter, setFilter] = useState('Tous');
  const [search, setSearch] = useState('');

  const filtered = posts.filter(p =>
    (filter === 'Tous' || p.category === filter) &&
    (p.title.toLowerCase().includes(search.toLowerCase()) || p.author.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPublished = posts.filter(p => p.status === 'published').length;
  const totalPending = posts.filter(p => p.status === 'pending').length;

  const approve = (id) => {
    setPosts(posts.map(p => p.id === id ? { ...p, status: 'published' } : p));
    toast.success('Publication approuvée');
  };

  const reject = (id) => {
    setPosts(posts.map(p => p.id === id ? { ...p, status: 'rejected' } : p));
    toast.success('Publication refusée');
  };

  const togglePin = (id) => {
    setPosts(posts.map(p => p.id === id ? { ...p, pinned: !p.pinned } : p));
    toast.success('Épinglage mis à jour');
  };

  const remove = (id) => {
    const p = posts.find(x => x.id === id);
    if (!window.confirm(`Supprimer ce post de « ${p.author} » ?`)) return;
    setPosts(posts.filter(x => x.id !== id));
    toast.success('Post supprimé');
  };

  return (
    <div className="p-6 space-y-6" data-testid="community-module">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-primary">Communauté</h2>
        <p className="text-sm text-muted-foreground font-body">Modération des posts et échanges entre aidants</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Posts publiés', value: totalPublished, Icon: CheckCircle, color: '#22863A' },
          { label: 'En attente', value: totalPending, Icon: AlertCircle, color: '#F47B20' },
          { label: 'Total membres', value: 48, Icon: Users, color: '#5A3B31' },
          { label: 'Vues ce mois', value: '1.2k', Icon: Eye, color: '#8B5CF6' },
        ].map(({ label, value, Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 border border-border shadow-sm flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: color + '18' }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div>
              <p className="font-heading text-xl font-semibold text-primary">{value}</p>
              <p className="text-xs text-muted-foreground font-body">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Moderation alert */}
      {totalPending > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="community-pending-alert">
          <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />
          <span className="text-sm font-body text-orange-800">
            <strong>{totalPending} publication(s)</strong> en attente de modération
          </span>
        </div>
      )}

      {/* Filters + Search */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…" className="pl-9 font-body rounded-full" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 rounded-full text-xs font-body font-medium transition-all ${filter === c ? 'bg-primary text-white' : 'bg-muted text-foreground/70 hover:bg-muted/80'}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Posts */}
      <div className="space-y-3">
        {filtered.map(p => (
          <div key={p.id} className={`bg-white rounded-2xl border shadow-sm p-5 hover:border-primary/20 transition-all ${p.status === 'pending' ? 'border-orange-200' : 'border-border'}`} data-testid={`post-item-${p.id}`}>
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                {p.avatar}
              </div>
              <div className="flex-1 min-w-0">
                {/* Title + badges */}
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  {p.pinned && <Pin className="w-3.5 h-3.5 text-[#F47B20] flex-shrink-0" />}
                  <h3 className="font-heading font-semibold text-primary text-sm leading-snug">{p.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CAT_COLORS[p.category] || 'bg-gray-100 text-gray-600'}`}>{p.category}</span>
                  <Badge className={p.status === 'published' ? 'bg-green-100 text-green-700 text-xs' : p.status === 'pending' ? 'bg-orange-100 text-orange-700 text-xs' : 'bg-red-100 text-red-700 text-xs'}>
                    {p.status === 'published' ? 'Publié' : p.status === 'pending' ? 'En attente' : 'Refusé'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground font-body mb-2 line-clamp-1 italic">"{p.excerpt}"</p>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground font-body">
                  <span className="font-medium">{p.author}</span>
                  <span>{new Date(p.date).toLocaleDateString('fr-FR')}</span>
                  <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{p.likes}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{p.comments} commentaires</span>
                </div>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {p.status === 'pending' && (
                  <>
                    <Button size="sm" onClick={() => approve(p.id)} className="rounded-full text-xs bg-green-600 hover:bg-green-700 text-white font-body px-3" data-testid={`post-approve-${p.id}`}>Approuver</Button>
                    <Button size="sm" variant="outline" onClick={() => reject(p.id)} className="rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50 font-body px-3" data-testid={`post-reject-${p.id}`}>Refuser</Button>
                  </>
                )}
                {p.status === 'published' && (
                  <button onClick={() => togglePin(p.id)} className={`p-2 rounded-xl transition-colors ${p.pinned ? 'bg-orange-100 text-orange-600' : 'hover:bg-muted text-muted-foreground'}`} title={p.pinned ? 'Désépingler' : 'Épingler'}>
                    <Pin className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => remove(p.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors" title="Supprimer" data-testid={`post-delete-${p.id}`}>
                  <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-muted-foreground font-body text-sm">Aucun post trouvé</div>
        )}
      </div>
    </div>
  );
}
